import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useStudioStore } from '@/store/useStudioStore';
import { useCreatorStore } from '@/store/useCreatorStore';
import { useChatCore } from '@/hooks/studio/useChatCore';
import { useChatService } from '@/hooks/studio/useChatService';
import { teardownSocket, socketActions } from '@/hooks/studio/useWebSocket';
import { Header } from '@/components/common/Header';
import { StudioSettingsDrawer } from '@/components/studio/StudioSettingsDrawer';
import { StudioHistoryModal } from '@/components/studio/StudioHistoryModal';
import { StudioHistoryListModal } from '@/components/studio/StudioHistoryListModal';
import { StudioPublishModal } from '@/components/studio/StudioPublishModal';
import { StudioPublishSuccessModal } from '@/components/studio/StudioPublishSuccessModal';
import { StudioPublishBanner } from '@/components/studio/StudioPublishBanner';
import { StudioDeviceSwitcher } from '@/components/studio/StudioDeviceSwitcher';
import { StudioPublishButton } from '@/components/studio/StudioPublishButton';
import { StudioSettingsButton } from '@/components/studio/StudioSettingsButton';
import { StudioVersionButton } from '@/components/studio/StudioVersionButton';
import { StudioVersionHistory } from '@/components/studio/StudioVersionHistory';
import { UserAccountMenu } from '@/components/common/UserAccountMenu';
import DashboardSiteCenterDialog from '@/components/dashboard/DashboardSiteCenterDialog';
import { buildDraftPreviewHtml } from '@/lib/domUtils';
import { useChatStore } from '@/store/useChatStore';
import { checkPreviewIsReady } from '@/lib/previewUtils.ts';
import { userApi } from '@/api/user.ts';
import { projectApi } from '@/api/project.ts';
import { isTechnicalGenerationSyncChangeNote, resolveEditableDraft } from '@/lib/draftUtils';
import { loadSpecialProjectPreviewHtml } from '@/lib/specialProjectPreview';
import {
  formatApiMessage,
  parseErrorString,
  isRoleDescription,
  removeChinese,
} from '@/lib/agentChatUtils';
import { Message } from '@/types/conversation.ts';
import { useSSEGenerate } from '@/hooks/studio/useSSEGenerate.ts';
import { isSpecialProjectType } from '@/lib/projectUtils';
import {
  bucketLength,
  getTrackingUrlHost,
  trackProductSalesEvent,
} from '@/lib/create/productSalesTracking';
import { trackStudioEvent } from '@/lib/studioTracking';
import { getTrackingErrorCode } from '@/lib/trackingUtils';
import { UpdateProjectParams, WebsiteVersionRecord, WebpageDraftListItem } from '@/types/project';
import type { HistoryItem } from '@/store/useStudioStore';
import {
  buildCloneGenerateRequestConfig,
  buildProductGenerateRequestConfig,
  INVALID_CLONE_SOURCE_URL_MESSAGE,
} from '@/lib/webpageGeneration';
import {
  STUDIO_SETTINGS_QUERY_KEY,
  STUDIO_SETTINGS_TAB_QUERY_KEY,
  STUDIO_SETTINGS_TAB_VALUES,
  STUDIO_SETTINGS_VALUES,
} from '@/lib/routeParams';
import { normalizeLegacyPlatformDomain } from '@/lib/platformDomain';
import { cn } from '@/lib/utils';
import {
  clearRememberedStudioProject,
  getRememberedStudioProject,
} from '@/lib/studioProjectContext';

const formatHistoryTimestamp = (value?: string) => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const normalizeHistorySummary = (value: string | undefined, fallback: string, max = 72) => {
  const text = (value || '').replace(/\s+/g, ' ').trim();
  if (!text) return fallback;
  return text.length > max ? `${text.slice(0, max - 3)}...` : text;
};

const normalizeRollbackCopy = (value?: string) => {
  const text = (value || '').trim();
  if (!text) return '';

  const rollbackFromToMatch = text.match(/^从\s*(v?[\d.]+)\s*回滚到\s*(v?[\d.]+)$/i);
  if (rollbackFromToMatch) {
    const [, fromVersion, toVersion] = rollbackFromToMatch;
    return `Rolled back from ${fromVersion} to ${toVersion}`;
  }

  const rollbackFromMatch = text.match(/^从\s*(v?[\d.]+)\s*回滚$/i);
  if (rollbackFromMatch) {
    const [, fromVersion] = rollbackFromMatch;
    return `Rolled back from ${fromVersion}`;
  }

  const rollbackToMatch = text.match(/^回滚到\s*(v?[\d.]+)$/i);
  if (rollbackToMatch) {
    const [, toVersion] = rollbackToMatch;
    return `Restored to ${toVersion}`;
  }

  return text;
};

const formatHistorySource = (flowType?: string | null) => {
  const normalized = String(flowType || '').trim();
  if (!normalized) return 'Manual edit';
  if (normalized === 'WEBPAGE_GENERATE_HTML_STANDARD') return 'Chat request';
  if (normalized === 'WEBPAGE_GENERATE_HTML_MODIFY') return 'Edit request';
  if (normalized === 'WEBPAGE_GENERATE_HTML_PATCH') return 'Selected element edit';
  if (normalized.includes('EXPERT')) return 'Expert review';
  if (normalized.includes('CREATOR')) return 'Creator';

  return normalized
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const isKeepCurrentResolutionNote = (value?: string | null) =>
  /^kept current version after review\.?$/i.test(String(value || '').trim());

const parsePositiveQueryNumber = (value: string | null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const studioSettingsTabSet = new Set<string>(Object.values(STUDIO_SETTINGS_TAB_VALUES));

const buildDraftHistoryDetail = (
  note: string,
  source: string,
  status: HistoryItem['status'],
  draft: WebpageDraftListItem,
) => {
  const headerLabel = source === 'Chat request' ? 'Chat request' : 'Change note';

  if (!note) {
    if (status === 'dismissed') {
      return `Kept the current draft instead of applying draft v${draft.version}.`;
    }
    if (status === 'review') {
      return `A proposed edited version is ready for draft v${draft.version}. Review it before deciding which version to keep.`;
    }
    if (status === 'pending') {
      return `Applying draft v${draft.version}. Check back in a moment for the final result.`;
    }
    return `Accepted and applied draft v${draft.version}.`;
  }

  if (status === 'dismissed') {
    return `${headerLabel}:\n${note}\n\nResult:\nReviewed, but the current draft was kept instead of applying the edited version.`;
  }
  if (status === 'review') {
    return `${headerLabel}:\n${note}\n\nResult:\nA proposed edited version is ready.\n\nNext step:\nUse the preview bar to compare before and after, then accept the edited version or keep the current one.`;
  }
  if (status === 'pending') {
    return `${headerLabel}:\n${note}\n\nStatus:\nApplying this request to the current draft preview.`;
  }
  return `${headerLabel}:\n${note}\n\nResult:\nAccepted and applied to the draft preview after review.`;
};

const mapDraftToHistoryItem = (
  draft: WebpageDraftListItem,
  previewReviewDraftId?: number | string | null,
  previewReviewActive = false,
): HistoryItem => {
  const status: HistoryItem['status'] =
    draft.draftStatus === 'ABANDONED'
      ? 'dismissed'
      : draft.draftStatus === 'PENDING'
        ? previewReviewActive && String(previewReviewDraftId) === String(draft.id)
          ? 'review'
          : 'pending'
        : 'done';
  const fallbackTitle =
    status === 'dismissed' ? `Kept current v${draft.version}` : `Draft v${draft.version}`;
  const note = normalizeRollbackCopy(draft.changeNote?.trim());
  const source = formatHistorySource(draft.flowType);

  return {
    id: `draft-${draft.id}`,
    title: normalizeHistorySummary(note, fallbackTitle),
    detail: buildDraftHistoryDetail(note, source, status, draft),
    status,
    source,
    at: formatHistoryTimestamp(draft.createTime),
  };
};

const shouldIncludeDraftHistoryItem = (draft: WebpageDraftListItem) => {
  // v1 is the initial generated baseline, not a post-generation change.
  if (Number(draft.version || 0) <= 1) return false;

  // "Keep current" creates a technical confirmed copy so latest-draft lookups stay stable.
  // That copy is not a user-facing content change and should not appear as a separate history row.
  if (draft.draftStatus === 'CONFIRMED' && isKeepCurrentResolutionNote(draft.changeNote)) {
    return false;
  }

  if (draft.draftStatus === 'CONFIRMED' && isTechnicalGenerationSyncChangeNote(draft.changeNote)) {
    return false;
  }

  return true;
};

const mapVersionToHistoryItem = (version: WebsiteVersionRecord): HistoryItem => {
  const versionLabel = version.semanticVersion
    ? `v${version.semanticVersion}`
    : `v${version.versionNumber}`;
  const description = normalizeRollbackCopy(version.description?.trim());

  return {
    id: `version-${version.id}`,
    title: normalizeHistorySummary(description, `Published ${versionLabel}`),
    detail:
      description ||
      `${versionLabel} was published on ${formatHistoryTimestamp(version.createTime) || version.createTime}.`,
    status: 'done',
    source: 'Publish',
    at: formatHistoryTimestamp(version.createTime),
  };
};

/**
 * Studio 工作台内部布局内容
 * 负责渲染 Header 及其内容，以及各种抽屉/弹窗。
 */
const StudioLayout = () => {
  const { t } = useTranslation('studio');
  const location = useLocation();
  const [rememberedStudioProject] = useState(getRememberedStudioProject);
  const {
    currentProjectId,
    currentProjectTypeId,
    currentId,
    setIframeUrl,
    setGeneratedHtml,
    setSessionId,
    setCurrentDraftMeta,
    startPreviewReview,
    clearPreviewReview,
    currentName,
    currentProject,
    currentSiteId,
    currentSiteInfo,
    sessionId,
    isBuilding,
    setIsBuilding,
    setPendingCommand,
    setHistoryItems,
    setSelectedHistoryItem,
    clearStudioState,
    isSettingsOpen,
    setSettingsOpen,
    setActiveSettingsTab,
    setCurrentProjectData,
    setSiteId,
    isHistoryOpen,
    setHistoryOpen,
    previewReview,
    toggleHistory,
    mobileWorkspaceView,
    setMobileWorkspaceView,
  } = useStudioStore();

  const { sendCommand } = useChatService();
  const { generateProject } = useSSEGenerate();
  const { clearMessages, appendMessagesBatch, setBuildError, isBuildFailed } = useChatStore();
  const studioRouteQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const projectId = parsePositiveQueryNumber(searchParams.get('projectId'));
    const userProjectId = parsePositiveQueryNumber(searchParams.get('userProjectId'));
    const tenantId = parsePositiveQueryNumber(searchParams.get('tenantId'));
    const siteId = parsePositiveQueryNumber(searchParams.get('siteId'));
    const shouldOpenSettings =
      searchParams.get(STUDIO_SETTINGS_QUERY_KEY) === STUDIO_SETTINGS_VALUES.page;

    return {
      projectId,
      userProjectId,
      tenantId,
      siteId,
      shouldOpenSettings,
      projectLoadKey: `${projectId || ''}:${userProjectId || ''}:${tenantId || ''}:${siteId || ''}`,
      hasProjectContext: Boolean(projectId || userProjectId),
    };
  }, [location.search]);
  const [isStudioContextReady, setStudioContextReady] = useState(
    () =>
      Boolean(currentProjectId) ||
      Boolean(rememberedStudioProject) ||
      studioRouteQuery.hasProjectContext,
  );
  const currentMatchesStudioRouteProject =
    studioRouteQuery.hasProjectContext &&
    ((studioRouteQuery.projectId &&
      String(currentProjectId || '') === String(studioRouteQuery.projectId)) ||
      (studioRouteQuery.userProjectId &&
        String(currentId || '') === String(studioRouteQuery.userProjectId)));
  const [failedStudioRouteProjectKey, setFailedStudioRouteProjectKey] = useState<string | null>(
    null,
  );
  const [failedStaticStudioSettingsFallback, setFailedStaticStudioSettingsFallback] =
    useState(false);
  const [isResourceLibraryOpen, setResourceLibraryOpen] = useState(false);
  const isLoadingStudioRouteProject =
    studioRouteQuery.hasProjectContext &&
    !currentMatchesStudioRouteProject &&
    failedStudioRouteProjectKey !== studioRouteQuery.projectLoadKey;
  const isLoadingStaticStudioSettingsFallback =
    studioRouteQuery.shouldOpenSettings &&
    !studioRouteQuery.hasProjectContext &&
    !currentProjectId &&
    !failedStaticStudioSettingsFallback;
  // 记录销毁 Socket 的定时器，用于应对 React 18 严格模式下的模拟卸载
  const teardownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 记录上一个初始化的项目ID，防止重复执行
  const lastInitializedIdRef = useRef<string | null>(null);
  const restoredPendingReviewKeyRef = useRef<string | null>(null);
  // 记录版本历史下拉容器 ref，用于点击外部关闭
  const historyRef = useRef<HTMLDivElement>(null);
  const lastHistoryProjectKeyRef = useRef<string | null>(null);

  // 挂载对话系统核心连接器（传入标识以满足 Hooks 规则）
  useChatCore(isSpecialProjectType(currentProjectTypeId));

  useEffect(() => {
    if (currentProjectId || !rememberedStudioProject) return;
    setCurrentProjectData(rememberedStudioProject);
  }, [currentProjectId, rememberedStudioProject, setCurrentProjectData]);

  useEffect(() => {
    if (currentProjectId || rememberedStudioProject || studioRouteQuery.hasProjectContext) {
      setStudioContextReady(true);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setStudioContextReady(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [currentProjectId, rememberedStudioProject, studioRouteQuery.hasProjectContext]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get(STUDIO_SETTINGS_QUERY_KEY) !== STUDIO_SETTINGS_VALUES.page) return;

    const requestedTab = (searchParams.get(STUDIO_SETTINGS_TAB_QUERY_KEY) || '')
      .trim()
      .toLowerCase();
    setActiveSettingsTab(
      studioSettingsTabSet.has(requestedTab) ? requestedTab : STUDIO_SETTINGS_TAB_VALUES.metadata,
    );
    if (!useStudioStore.getState().isSettingsOpen) {
      trackStudioEvent('studio_settings_open', {
        entrySource: 'route_param',
        defaultTab: studioSettingsTabSet.has(requestedTab)
          ? requestedTab
          : STUDIO_SETTINGS_TAB_VALUES.metadata,
      });
    }
    setSettingsOpen(true);
  }, [location.search, setActiveSettingsTab, setSettingsOpen]);

  useEffect(() => {
    if (!studioRouteQuery.hasProjectContext) {
      setFailedStudioRouteProjectKey(null);
      return;
    }

    if (currentMatchesStudioRouteProject) {
      setFailedStudioRouteProjectKey(null);
      if (studioRouteQuery.siteId && currentSiteId !== String(studioRouteQuery.siteId)) {
        setSiteId(String(studioRouteQuery.siteId));
      }
      return;
    }

    let cancelled = false;
    setFailedStudioRouteProjectKey(null);

    projectApi
      .getProjectDetail({
        ...(studioRouteQuery.projectId ? { projectId: studioRouteQuery.projectId } : {}),
        ...(studioRouteQuery.userProjectId
          ? { userProjectId: studioRouteQuery.userProjectId }
          : {}),
        ...(studioRouteQuery.tenantId ? { tenantId: studioRouteQuery.tenantId } : {}),
      })
      .then((project) => {
        if (cancelled) return;
        setCurrentProjectData(project);
        if (studioRouteQuery.siteId) {
          setSiteId(String(studioRouteQuery.siteId));
        }
      })
      .catch((error) => {
        console.error('[StudioLayout] Failed to load route project context:', error);
        if (!cancelled) {
          setFailedStudioRouteProjectKey(studioRouteQuery.projectLoadKey);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentMatchesStudioRouteProject,
    currentSiteId,
    setCurrentProjectData,
    setSiteId,
    studioRouteQuery.hasProjectContext,
    studioRouteQuery.projectId,
    studioRouteQuery.projectLoadKey,
    studioRouteQuery.siteId,
    studioRouteQuery.tenantId,
    studioRouteQuery.userProjectId,
  ]);

  useEffect(() => {
    if (
      !studioRouteQuery.shouldOpenSettings ||
      studioRouteQuery.hasProjectContext ||
      currentProjectId
    ) {
      setFailedStaticStudioSettingsFallback(false);
      return;
    }

    let cancelled = false;
    setFailedStaticStudioSettingsFallback(false);

    projectApi
      .getProjectList()
      .then((projects) => {
        if (cancelled) return;

        const fallbackProject =
          projects.find((project) => project.sitePaths?.some((sitePath) => sitePath.siteId)) ||
          projects[0];
        if (!fallbackProject) {
          setFailedStaticStudioSettingsFallback(true);
          return;
        }

        setCurrentProjectData(fallbackProject);
      })
      .catch((error) => {
        console.error('[StudioLayout] Failed to load static settings route project:', error);
        if (!cancelled) {
          setFailedStaticStudioSettingsFallback(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentProjectId,
    setCurrentProjectData,
    studioRouteQuery.hasProjectContext,
    studioRouteQuery.shouldOpenSettings,
  ]);

  useEffect(() => {
    if (!isHistoryOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (historyRef.current && !historyRef.current.contains(e.target as Node)) {
        toggleHistory();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isHistoryOpen, toggleHistory]);

  useEffect(() => {
    if (previewReview.active && isHistoryOpen) {
      setHistoryOpen(false);
    }
  }, [isHistoryOpen, previewReview.active, setHistoryOpen]);

  // 项目名称本地状态
  const [localName, setLocalName] = useState(currentName);

  useEffect(() => {
    setLocalName(currentName);
  }, [currentName]);

  useEffect(() => {
    setResourceLibraryOpen(false);
  }, [currentId, currentProjectId]);

  useEffect(() => {
    let cancelled = false;
    const historyProjectKey = `${currentProjectTypeId || ''}:${currentProjectId || ''}:${currentId || ''}`;
    const scopeChanged = lastHistoryProjectKeyRef.current !== historyProjectKey;
    lastHistoryProjectKeyRef.current = historyProjectKey;

    const loadHistoryItems = async () => {
      try {
        if (isSpecialProjectType(currentProjectTypeId)) {
          if (!currentProjectId) return;
          const versions = await projectApi.getProjectVersions({
            projectId: Number(currentProjectId),
          });
          if (cancelled) return;
          setHistoryItems(versions.map(mapVersionToHistoryItem));
          return;
        }

        if (!currentId) return;
        const drafts = await projectApi.listDrafts({
          userProjectId: Number(currentId),
          pageNumber: 1,
          pageSize: 100,
          draftStatuses: ['PENDING', 'CONFIRMED', 'ABANDONED'],
        });

        if (cancelled) return;

        const items = (drafts.list || [])
          .slice()
          .sort((a, b) => {
            const timeDiff = new Date(b.createTime).getTime() - new Date(a.createTime).getTime();
            if (timeDiff !== 0) return timeDiff;
            return b.version - a.version;
          })
          .filter(shouldIncludeDraftHistoryItem)
          .map((draft) =>
            mapDraftToHistoryItem(draft, previewReview.draftId, previewReview.active),
          );

        setHistoryItems(items);
      } catch (error) {
        if (!cancelled) {
          console.error('[StudioLayout] Failed to load history items:', error);
          setHistoryItems([]);
        }
      }
    };

    if (currentProjectId) {
      if (scopeChanged) {
        setSelectedHistoryItem(null);
        setHistoryItems([]);
      }
      loadHistoryItems();
    } else {
      setSelectedHistoryItem(null);
      setHistoryItems([]);
    }

    return () => {
      cancelled = true;
    };
  }, [
    currentId,
    currentProjectId,
    currentProjectTypeId,
    previewReview.active,
    previewReview.draftId,
    setHistoryItems,
    setSelectedHistoryItem,
  ]);

  useEffect(() => {
    const creatorStore = useCreatorStore.getState();
    creatorStore.setSelectedElement(null);
    creatorStore.setPromptInput('');
    creatorStore.setEditing(false);
    creatorStore.setLinkerHost(null);
  }, [currentProjectId, currentId]);

  const handleNameBlur = async () => {
    const finalName = localName.trim();
    if (!finalName || finalName === currentName) {
      setLocalName(currentName);
      return;
    }
    try {
      if (currentProjectId) {
        const params: UpdateProjectParams = { projectName: finalName };
        if (isSpecialProjectType(currentProjectTypeId)) {
          params.projectId = Number(currentProjectId);
        } else {
          params.userProjectId = Number(currentId);
        }

        const updatedProject = await projectApi.updateProject(params);

        trackStudioEvent('studio_project_name_update_result', {
          result: 'success',
          nameLength: finalName.length,
          projectTypeId: currentProjectTypeId,
        });

        setLocalName(finalName);
        useStudioStore.setState((state) => ({
          currentName: finalName,
          currentProject: state.currentProject
            ? {
                ...state.currentProject,
                ...(updatedProject || {}),
                projectName: finalName,
              }
            : updatedProject
              ? { ...updatedProject, projectName: finalName }
              : state.currentProject,
        }));
      }
    } catch (err) {
      trackStudioEvent('studio_project_name_update_result', {
        result: 'failure',
        nameLength: finalName.length,
        projectTypeId: currentProjectTypeId,
        errorCode: getTrackingErrorCode(err),
      });
      console.error('Failed to update project name', err);
      setLocalName(currentName);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setLocalName(currentName);
      e.currentTarget.blur();
    }
  };

  // 检测socket挂载情况
  useEffect(() => {
    const initialize = () => {
      // 检查是否为严格模式下的重新挂载
      const isRemounting = !!teardownTimerRef.current;

      if (isRemounting) {
        if (teardownTimerRef.current) {
          clearTimeout(teardownTimerRef.current);
        }
        teardownTimerRef.current = null;
        // 如果是重挂载，说明之前的 initialize 已经执行或正在执行，此处直接返回以防止双倍请求
        return;
      }

      // 只有在真正的首次进入时，才清理消息
      console.log('[StudioLayout] 首次挂载，清理旧会话消息');
      clearMessages();
    };

    initialize();

    return () => {
      console.log('[StudioLayout] 准备清理 Socket...');
      // 延迟执行销毁，防止 React 18 严格模式下的模拟卸载导致连接中断
      teardownTimerRef.current = setTimeout(() => {
        console.log('[StudioLayout] 执行物理卸载，正在真正销毁 Socket 并重置状态...');
        teardownSocket();
        clearStudioState();
        clearRememberedStudioProject();
        teardownTimerRef.current = null;
        // 清除初始化标记，允许下次重新进入时触发
        lastInitializedIdRef.current = null;
      }, 100);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 处理从 Creation 页面传递过来的初始构建指令
  useEffect(() => {
    const initStudio = async () => {
      if (!currentProjectId) return;

      // 如果当前 ID 已经初始化过，则跳过
      if (lastInitializedIdRef.current === currentProjectId) {
        console.log('[StudioLayout] 项目已初始化，跳过重复执行:', currentProjectId);
        return;
      }

      // 立即记录，防止并发
      lastInitializedIdRef.current = currentProjectId;

      // 直接从 Store 获取最新状态，防止 React 严格模式下的闭包旧值问题

      const useStudioStoreData = useStudioStore.getState();
      const command = useStudioStoreData.pendingCommand;
      const buildVariant = useStudioStoreData.buildVariant;
      const cloneSourceUrl = useStudioStoreData.cloneSourceUrl;
      const productRequestConfig = useStudioStoreData.productRequestConfig;

      if (command) {
        if (isSpecialProjectType(currentProjectTypeId)) {
          // 通过 WebSocket 发送指令
          socketActions.auth(sessionId).then(() => {
            sendCommand(command);
          });
        } else {
          const cloneRequestConfig =
            buildVariant === 'clone' ? buildCloneGenerateRequestConfig(cloneSourceUrl) : null;
          const requestConfig =
            buildVariant === 'product'
              ? productRequestConfig || buildProductGenerateRequestConfig()
              : cloneRequestConfig;

          if (buildVariant === 'clone' && !cloneRequestConfig) {
            console.error('[StudioLayout] Invalid clone source URL:', cloneSourceUrl);
            setBuildError(true, INVALID_CLONE_SOURCE_URL_MESSAGE);
            setIsBuilding(false);
            setPendingCommand(null);
            return;
          }

          // 通过 SSE 流式生成
          if (buildVariant === 'product') {
            trackProductSalesEvent('product_sales_studio_generation_start', {
              projectId: currentProjectId || '',
              userProjectId: currentId || '',
              buildVariant,
              flowType: requestConfig?.flowType || '',
              generateTaskCode: String(requestConfig?.flowOptions?.generateTaskCode || ''),
              hasProductRequestConfig: Boolean(productRequestConfig),
              sourceUrlHost: getTrackingUrlHost(
                typeof requestConfig?.flowOptions?.sourceUrl === 'string'
                  ? requestConfig.flowOptions.sourceUrl
                  : '',
              ),
              pendingCommandLengthBucket: bucketLength(command.length),
            });
          }
          generateProject(command, undefined, undefined, requestConfig ?? undefined);
        }
      } else {
        if (isSpecialProjectType(currentProjectTypeId)) {
          // 查看页面 - 获取
          const iframeUrl = await checkPreviewIsReady(currentProjectId);
          if (iframeUrl) {
            const localPreviewHtml = await loadSpecialProjectPreviewHtml(currentProjectId);
            setIframeUrl(localPreviewHtml ? null : iframeUrl);
            setGeneratedHtml(localPreviewHtml ? buildDraftPreviewHtml(localPreviewHtml) : '');
            setIsBuilding(false);
            useCreatorStore.getState().setCreating(false);
            useCreatorStore.getState().setEditing(false);
          } else {
            // 页面不存在- 获取历史记录
            try {
              const sessionRes = await userApi.getSessions({
                projectId: Number(currentProjectId),
                limit: 1,
              });

              if (sessionRes.sessions && sessionRes.sessions.length > 0) {
                const sid = sessionRes.sessions[0].id;
                console.log('[StudioLayout] 自动关联到最近会话:', sid);

                socketActions.auth(sid);
                setSessionId(sid);

                const { messages = [] } = await userApi.getMessages({
                  projectId: Number(currentProjectId),
                  sessionId: sid,
                });

                let formattedMessages: Record<string, unknown>[] = [];

                if (messages.length > 0) {
                  formattedMessages = messages
                    .map((m: Record<string, unknown>) => {
                      const formatted = formatApiMessage(m);
                      return {
                        ...formatted,
                        content: removeChinese((formatted.content as string) || ''),
                      };
                    })
                    .filter((msg) => {
                      const content = (msg.content as string) || '';
                      return content.trim() !== '' && !isRoleDescription(content);
                    });

                  const startTs = new Date(
                    (formattedMessages[0]?.timestamp as string | number | Date) ?? 0,
                  ).getTime();
                  const endTs = new Date(
                    (formattedMessages[formattedMessages.length - 1]?.timestamp as
                      | string
                      | number
                      | Date) ?? 0,
                  ).getTime();
                  const diffSecs = Math.max(0, Math.floor((endTs - startTs) / 1000));

                  useChatStore.getState().setElapsedSeconds(diffSecs);

                  const lastMsg = formattedMessages[formattedMessages.length - 1];
                  const text = lastMsg?.content ?? '';
                  const errorInfo = parseErrorString(text as string);
                  if (errorInfo.isError) {
                    setIsBuilding(true);
                    setBuildError(true, errorInfo.message);
                    return;
                  }
                  appendMessagesBatch(formattedMessages as Partial<Message>[]);
                }

                setIsBuilding(true);
                setIframeUrl(null);
              }
            } catch (err) {
              console.error('[StudioLayout] 旧模式会话加载失败:', err);
              setIsBuilding(true);
            }
          }
        } else {
          // 查看草稿
          const userProjectId = Number(currentId);
          console.log('[StudioLayout] AI 网页模式，尝试恢复最新草稿预览');
          try {
            const { editableDraft, pendingReview } = await resolveEditableDraft(userProjectId);

            if (pendingReview) {
              clearPreviewReview();
              setGeneratedHtml(pendingReview.beforeHtml);
              setCurrentDraftMeta(
                pendingReview.currentDraft.id,
                pendingReview.currentDraft.version,
                pendingReview.currentDraft.draftStatus,
              );
              startPreviewReview({
                draftId: pendingReview.pendingDraft.id,
                version: pendingReview.pendingDraft.version,
                beforeHtml: pendingReview.beforeHtml,
                afterHtml: pendingReview.afterHtml,
                title: 'Review latest change',
                note: 'Switch between the current draft and the proposed edit, then decide which version to keep.',
                sourceMessageId: null,
              });
              setIsBuilding(false);
              useCreatorStore.getState().setCreating(false);
              useCreatorStore.getState().setEditing(false);
            } else if (editableDraft?.content) {
              clearPreviewReview();
              setGeneratedHtml(buildDraftPreviewHtml(editableDraft.content));
              setCurrentDraftMeta(
                editableDraft.id,
                editableDraft.version,
                editableDraft.draftStatus,
              );
              setIsBuilding(false);
              useCreatorStore.getState().setCreating(false);
              useCreatorStore.getState().setEditing(false);
            } else {
              setIsBuilding(true);
            }
          } catch {
            setIsBuilding(true);
          }
        }
      }
    };

    initStudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clearPreviewReview,
    currentId,
    currentProjectId,
    setCurrentDraftMeta,
    setGeneratedHtml,
    setIsBuilding,
    startPreviewReview,
  ]);

  useEffect(() => {
    if (!currentProjectId || !currentId || isSpecialProjectType(currentProjectTypeId)) {
      restoredPendingReviewKeyRef.current = null;
      return;
    }

    const restoreKey = `${currentProjectTypeId || ''}:${currentProjectId}:${currentId}`;
    if (previewReview.active || restoredPendingReviewKeyRef.current === restoreKey) {
      return;
    }

    let cancelled = false;

    const restorePendingReview = async () => {
      try {
        const { pendingReview } = await resolveEditableDraft(Number(currentId));
        if (cancelled || !pendingReview) return;

        clearPreviewReview();
        setGeneratedHtml(pendingReview.beforeHtml);
        setCurrentDraftMeta(
          pendingReview.currentDraft.id,
          pendingReview.currentDraft.version,
          pendingReview.currentDraft.draftStatus,
        );
        startPreviewReview({
          draftId: pendingReview.pendingDraft.id,
          version: pendingReview.pendingDraft.version,
          beforeHtml: pendingReview.beforeHtml,
          afterHtml: pendingReview.afterHtml,
          title: 'Review latest change',
          note: 'Switch between the current draft and the proposed edit, then decide which version to keep.',
          sourceMessageId: null,
        });
      } finally {
        if (!cancelled) {
          restoredPendingReviewKeyRef.current = restoreKey;
        }
      }
    };

    void restorePendingReview();

    return () => {
      cancelled = true;
    };
  }, [
    clearPreviewReview,
    currentId,
    currentProjectId,
    currentProjectTypeId,
    previewReview.active,
    setCurrentDraftMeta,
    setGeneratedHtml,
    startPreviewReview,
  ]);

  const studioResourceSiteId = useMemo(() => {
    const siteId = Number(
      currentSiteInfo?.id ??
        (currentSiteId ? Number(currentSiteId) : null) ??
        currentProject?.sitePaths?.[0]?.siteId ??
        0,
    );
    return Number.isFinite(siteId) && siteId > 0 ? siteId : null;
  }, [currentProject, currentSiteId, currentSiteInfo]);

  const studioResourceSiteDomain = useMemo(() => {
    return (
      normalizeLegacyPlatformDomain(
        currentSiteInfo?.domain ||
          currentSiteInfo?.originalDomain ||
          currentProject?.sitePaths?.[0]?.domain ||
          currentProject?.sitePaths?.[0]?.originalDomain ||
          '',
      ) || 'this site'
    );
  }, [currentProject, currentSiteInfo]);

  if (
    !isStudioContextReady ||
    isLoadingStudioRouteProject ||
    isLoadingStaticStudioSettingsFallback ||
    (!currentProjectId && rememberedStudioProject)
  ) {
    return null;
  }

  if (
    !currentProjectId ||
    (studioRouteQuery.hasProjectContext && !currentMatchesStudioRouteProject)
  ) {
    return <Navigate to="/create" state={{ from: location }} replace />;
  }

  /** 左侧插槽：项目名称输入与状态标签 */
  const leftContent = (
    <div className="flex items-center gap-0">
      <div className="flex h-[32px] items-center gap-[10px] rounded-lg bg-black/5 px-[10px] transition-colors focus-within:bg-black/10">
        <input
          value={localName}
          onChange={(e) => setLocalName(e.target.value)}
          onBlur={handleNameBlur}
          onKeyDown={handleNameKeyDown}
          title={localName}
          className="studio-project-name-input w-[112px] truncate bg-transparent text-[13px] font-bold text-[#0D0D1A] outline-none placeholder:text-[#0D0D1A]/40 sm:w-[160px]"
          placeholder={t('preview.project_name_placeholder', 'Project Name')}
        />
      </div>

      <div
        id="statusChip"
        className="studio-status-chip ml-[6px] inline-flex h-[22px] items-center gap-[5px] rounded-full bg-emerald-500/10 px-[10px] py-[3px] text-[11px] font-bold text-emerald-600"
      >
        <div className="animate-live-glow h-[5px] w-[5px] rounded-full bg-current" />
        {!isBuilding && currentSiteId ? t('preview.status_live') : t('preview.status_draft')}
      </div>
    </div>
  );

  /** 中间插槽：设备切换 */
  const centerContent = (
    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
      <StudioDeviceSwitcher />
      {!isBuilding && !isBuildFailed ? (
        <div className="studio-mobile-switch" role="tablist" aria-label="Compact Studio sections">
          <button
            type="button"
            role="tab"
            aria-selected={mobileWorkspaceView === 'edit'}
            className={cn('studio-mobile-tab', mobileWorkspaceView === 'edit' && 'active')}
            onClick={() => setMobileWorkspaceView('edit')}
          >
            <Icon icon="fa6-solid:message" className="text-[11px]" />
            Edit
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mobileWorkspaceView === 'preview'}
            className={cn('studio-mobile-tab', mobileWorkspaceView === 'preview' && 'active')}
            onClick={() => setMobileWorkspaceView('preview')}
          >
            <Icon icon="fa6-solid:eye" className="text-[11px]" />
            Preview
          </button>
        </div>
      ) : null}
    </div>
  );

  /** 右侧插槽：功能按钮组 */
  const rightContent = (
    <div className="studio-header-actions relative flex shrink-0 items-center gap-2 sm:gap-3">
      <div className="studio-publish-control">
        <StudioPublishButton />
      </div>
      <div className="studio-history-control relative" ref={historyRef}>
        <StudioVersionButton />
        <StudioVersionHistory isOpen={isHistoryOpen} onClose={toggleHistory} />
      </div>
      <div className="studio-resource-control">
        <button
          title="Resources"
          onClick={() => setResourceLibraryOpen(true)}
          className={`text-muted-foreground/60 hover:text-foreground flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-[9px] border border-black/5 bg-white text-[14px] transition-all hover:border-[rgba(109,40,217,0.3)] hover:bg-[rgba(109,40,217,0.05)] hover:text-[#6D28D9] ${
            isResourceLibraryOpen ? 'border-[#6D28D9]/30 bg-[#6D28D9]/5 text-[#6D28D9]' : ''
          }`}
        >
          <Icon icon="fa6-solid:photo-film" />
        </button>
      </div>
      <div className="studio-settings-control">
        <StudioSettingsButton />
      </div>
      <div className="studio-account-control">
        <UserAccountMenu triggerVariant="studio" />
      </div>
    </div>
  );

  return (
    <div className="bg-background selection:bg-primary/10 flex h-full flex-col overflow-hidden">
      {/* 顶部导航栏 */}
      <Header
        variant="studio"
        leftContent={leftContent}
        centerContent={centerContent}
        rightContent={rightContent}
      />

      <StudioPublishBanner />

      {/* 内容区域：通常是 Studio 主页面 */}
      <main className="dark:bg-background/50 bg-background relative flex min-w-0 flex-1 flex-col overflow-hidden">
        <Outlet />
      </main>

      {/* 项目设置抽屉 */}
      <StudioSettingsDrawer isOpen={isSettingsOpen} onClose={() => setSettingsOpen(false)} />

      <DashboardSiteCenterDialog
        campaignEnabled={false}
        open={isResourceLibraryOpen}
        siteId={studioResourceSiteId}
        siteDomain={studioResourceSiteDomain}
        onClose={() => setResourceLibraryOpen(false)}
      />

      {/* 历史版本详情模态框 */}
      <StudioHistoryModal />

      {/* 历史记录列表模态框 */}
      <StudioHistoryListModal />

      {/* 发布确认模态框 */}
      <StudioPublishModal />

      {/* 发布成功模态框 */}
      <StudioPublishSuccessModal />
    </div>
  );
};

export default StudioLayout;
